import { LayoutContext } from '@landsbankinn/slices/components/contexts/LayoutContext';
import type { Router } from 'next/router';
import React, { useContext, useRef } from 'react';
import { a, useSpring, useTransition } from 'react-spring';
import { PageTransitionContext } from 'lib/page-transition';

interface IProps {
  children: React.ReactNode;
  router: Router;
}

export function PageTransition({ children, router }: IProps) {
  const { queue, targets } = useContext(PageTransitionContext);
  const { setIsNavigationOpen } = useContext(LayoutContext);
  const transition = useRef<any>();
  const item = useRef<any>();

  const [style, spring] = useSpring(() => ({
    x: 0,
    y: 0,
    width: 10,
    height: 10,
    opacity: 0,
  }));

  const transitions = useTransition(children, {
    key: () => router.asPath?.split('?')[0] || router.route,
    enter: () => async (next) => {
      // transition behaviour
      if (transition.current) {
        await transition.current;
        transition.current = null;
        if (item.current) {
          const { a } = item.current;
          spring.start({
            width: a.width,
            height: a.height,
            y: a.top,
            x: a.left,
            opacity: 1,
            immediate: true,
          });
          // wait for new page to render
          // @todo find a better solution
          await new Promise((r) => setTimeout(r, 70));
          next({ opacity: 0, immediate: true });
          const b = targets.get(item.current.to).current;
          const bRect = b.getBoundingClientRect();

          spring.set({
            x: Math.round(bRect.left),
            y: Math.round(bRect.top),
            width: bRect.width,
            height: bRect.height,
          });
          spring.set({ opacity: 0 });
          await next({ opacity: 1 });
          await next({ position: 'relative', immediate: true });
          item.current = null;
          return;
        }
      }

      // default behaviour
      await next({ opacity: 1, immediate: true });
    },
    leave: () => async (next) => {
      setIsNavigationOpen(false);
      if (queue.length) {
        transition.current = new Promise(async (resolve) => {
          await next({ position: 'absolute', immediate: true });
          const { from, to } = queue.shift();
          const a = from.current.getBoundingClientRect();
          item.current = { a, to };
          resolve(void 0);
        });
        await next({ opacity: 0 });
        return;
      }

      // default behaviour
      await next({ opacity: 0, immediate: true });
    },
    from: { opacity: 1, position: 'relative', width: '100%', left: 0 },
  });

  return (
    <>
      {transitions((values, item) => (
        <a.div style={values as any}>{item}</a.div>
      ))}
    </>
  );
}
